import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { AddCircleOutline, Delete, Edit } from "@mui/icons-material";
import { Button, IconButton, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import MasterViewController from "../../redux/actions/masterApiActions";
import DepartmentFormModal from "./AddDepartment";
import ExportButton from "../../components/ag grid/ExportButton";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx } from "../../components/auth/authTheme";
import usePermission from "../../utils/usePermission";

import "../../styles/styles.css";
import "../../styles/layout.css";

const DepartmentList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const { hasPermission } = usePermission();

  const [showModal, setShowModal] = useState(false);
  const [editDepartment, setEditDepartment] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);

  const { getDepartments, deleteDepartment } = MasterViewController();
  const { departments } = useSelector((state) => state.master);

  const canCreate = hasPermission("departments", "create");
  const canEdit = hasPermission("departments", "edit");
  const canDelete = hasPermission("departments", "delete");

  useEffect(() => {
    dispatch(getDepartments());
  }, [dispatch]);

  const handleDelete = useCallback(
    (id) => dispatch(deleteDepartment(id)),
    [dispatch, deleteDepartment]
  );

  const handleEdit = useCallback((department) => {
    setEditDepartment(department);
    setShowModal(true);
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
      },
      {
        headerName: "SR.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Department Name",
        field: "name",
        flex: 1,
        minWidth: 200,
      },
      ...(canEdit || canDelete
        ? [
            {
              headerName: "Actions",
              field: "actions",
              pinned: "right",
              width: 120,
              minWidth: 120,
              maxWidth: 120,
              suppressSizeToFit: true,
              sortable: false,
              cellRenderer: (params) => (
                <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
                  {canEdit && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(params.data)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        sx={{ color: "#d32f2f" }}
                        onClick={() => handleDelete(params.data.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canEdit, canDelete, handleEdit, handleDelete]
  );

  return (
    <ListPageLayout
      title="Departments"
      subtitle="Manage organization departments for employee assignment"
      headerAction={
        canCreate ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={() => {
              setEditDepartment(null);
              setShowModal(true);
            }}
            sx={toolbarButtonSx}
          >
            Add Department
          </Button>
        ) : null
      }
      toolbarRight={
        <>
          <ExportButton
            gridRef={gridRef}
            fileName="departments.xlsx"
            exportColumns={["name"]}
            disabled={selectedRows.length === 0}
          />
          <PageSizeSelector
            pageSize={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          />
        </>
      }
    >
      <div className="list-page-grid">
        <ReusableGrid
          rowData={departments}
          columnDefs={columnDefs}
          gridRef={gridRef}
          pagination={true}
          pageSize={pageSize}
          onSelectionChanged={(rows) => setSelectedRows(rows)}
          emptyMessage="No departments yet"
          emptyHint={
            canCreate
              ? 'Click "Add Department" to create your first department.'
              : "No department records are available."
          }
        />
      </div>

      {showModal && (
        <DepartmentFormModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          initialData={editDepartment}
        />
      )}
    </ListPageLayout>
  );
};

export default DepartmentList;
